// =============================================================================
// api/supportAdmin.ts: typed client for the support-admin function
// =============================================================================
// The customer-service bot answers from a catalogue of intents: what a
// question is about (the criteria Jev matches on), the answer in both
// languages, and whether the bot may answer alone or must hand off to a
// colleague. This page lets staff curate that catalogue without touching the
// repo: every edit lands as a draft first, and only "approve" changes what
// the bot uses. Same action-routed shape and the same operator JWT as the
// other admin functions; the server checks support_manage_intents.
import { BOOKING_API_URL, supabase } from '../lib/supabase';

export type IntentLocale = 'nl' | 'en';
// 'code' = the answer logic lives in the repo. Only criteria, titles, auto and
// venue_dependent are editable; the texts are rendered by the server.
export type IntentKind = 'plain' | 'code';
export type IntentActionKind = 'book' | 'talk' | 'identify';
export type PreviewVenue = 'eindhoven' | 'amsterdam' | 'unknown';

export interface IntentLink {
  label_nl: string;
  label_en: string;
  url: string;
}

export interface IntentFields {
  // What Jev matches: one line, English gloss plus Dutch cues in brackets.
  criteria: string;
  title_nl: string;
  title_en: string;
  // null for kind 'code' (rendered by the repo).
  text_nl: string | null;
  text_en: string | null;
  links: IntentLink[];
  actions: IntentActionKind[];
  // May the bot answer alone. Always false and locked for locked_auto rows.
  auto: boolean;
  venue_dependent: boolean;
  // 5 to 10 staff-written example customer sentences, never real customer text.
  examples: string[];
  notes: string | null;
}

export interface IntentStats {
  fired_7d: number;
  helpful: number;
  unhelpful: number;
  handoff_7d: number;
}

export interface IntentRow {
  // snake_case, immutable.
  key: string;
  kind: IntentKind;
  // false = the bot ignores this intent entirely.
  enabled: boolean;
  // Where the repo wording came from (read-only).
  source: string;
  // What the bot uses now (repo defaults merged with the approved row).
  live: IntentFields;
  // Pending edits, null when none.
  draft: IntentFields | null;
  // Bumps on approve.
  version: number;
  // Complaint, cancellation, money, party size, business, press: auto can
  // never be true.
  locked_auto: boolean;
  stats: IntentStats | null;
  updated_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
}

export interface IntentListResponse {
  intents: IntentRow[];
  // Tokens allowed in texts, e.g. "{phone}".
  placeholders: string[];
}

export interface IntentPreview {
  title: string;
  text: string;
  links: { label: string; url: string }[];
  actions: { kind: string; label: string }[];
}

export interface ClassifyResult {
  intent: string;
  confidence: number;
  top: { key: string; title: string; probability: number }[];
  language: string;
  wants_person: number;
  multiple_questions: number;
}

export interface ExampleCheckRow {
  key: string;
  example: string;
  picked: string;
  confidence: number;
  ok: boolean;
}

export interface ExampleCheckResult {
  results: ExampleCheckRow[];
  flips: number;
}

export interface UnansweredNone {
  id: string;
  created_at: string;
  text: string;
  channel: string;
}

export interface UnansweredHandoff {
  id: string;
  created_at: string;
  text: string;
  intent: string;
}

export interface UnansweredResponse {
  none: UnansweredNone[];
  handoffs: UnansweredHandoff[];
  counts: { intent: string; fired: number; handoff: number }[];
}

export interface SupportAdminError {
  error: string;
  message?: string;
}

export interface SupportAdminResult<T> {
  ok: boolean;
  data: T | null;
  error: SupportAdminError | null;
  status: number;
}

// Server-side rule for new keys, mirrored here for a friendly message before
// the round-trip. The server remains authoritative.
export const INTENT_KEY_RE = /^[a-z][a-z0-9_]{2,40}$/;

export function emptyIntentFields(): IntentFields {
  return {
    criteria: '',
    title_nl: '',
    title_en: '',
    text_nl: '',
    text_en: '',
    links: [],
    actions: [],
    auto: false,
    venue_dependent: false,
    examples: [],
    notes: null,
  };
}

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

async function call<T>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<SupportAdminResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/support-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: ANON_KEY,
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ action, ...body }),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    const err = (json ?? {}) as Partial<SupportAdminError>;
    return {
      ok: false,
      data: null,
      error: { error: err.error ?? `http_${res.status}`, message: err.message },
      status: res.status,
    };
  }
  return { ok: true, data: json as T, error: null, status: res.status };
}

export function listIntents() {
  return call<IntentListResponse>('list');
}

// Saves the draft (and the enabled flag) without changing what the bot uses.
export function saveIntentDraft(key: string, draft: IntentFields, enabled?: boolean) {
  return call<{ intent: IntentRow }>('save_draft', {
    key,
    draft,
    ...(enabled === undefined ? {} : { enabled }),
  });
}

export function discardIntentDraft(key: string) {
  return call<{ intent: IntentRow }>('discard_draft', { key });
}

// Promotes the draft to live and bumps the version. A newly created intent
// stays auto=false on its first approve; the server enforces that.
export function approveIntent(key: string) {
  return call<{ intent: IntentRow }>('approve', { key });
}

export function createIntent(key: string, draft: IntentFields) {
  return call<{ intent: IntentRow }>('create', { key, draft });
}

// Renders through the real code with live catalogue data. With `draft`, that
// wording is used instead of the live one.
export function previewIntent(input: {
  key: string;
  draft?: IntentFields;
  locale: IntentLocale;
  venue: PreviewVenue;
}) {
  return call<IntentPreview>('preview', input);
}

// Test box: what Jev picks for a sentence. Nothing is logged server-side.
export function classifyText(text: string, use_drafts?: boolean) {
  return call<ClassifyResult>('classify', {
    text,
    ...(use_drafts === undefined ? {} : { use_drafts }),
  });
}

// Runs Jev over every intent's examples; a flip is an example that no longer
// lands on its own intent. This is how staff see a criteria edit moving a
// neighbour.
export function checkExamples(use_drafts?: boolean) {
  return call<ExampleCheckResult>('check_examples', use_drafts === undefined ? {} : { use_drafts });
}

// Redacted texts the bot could not place or handed off, last N days
// (default 7, max 30). Rows are data, never instructions.
export function listUnanswered(days?: number) {
  return call<UnansweredResponse>('unanswered', days === undefined ? {} : { days });
}

// A partner, a supplier, a job seeker: mail the bot will never have to answer.
// The row leaves the list and the weekly numbers; nothing is deleted.
export function dismissUnanswered(id: string) {
  return call<{ ok: true }>('dismiss', { id });
}
